import React from 'react';
import { useAuth } from '@/contexts/auth-context';
import { useExcel } from '@/hooks/queries';
import api from '@/lib/api';

interface UsePasteConfigProps {
  columns: any[];
  onNonWritableFields?: (fields: Set<string>) => void;
  onPasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<any[]>; resultField?: string }>
  ) => void;
}

export const usePasteConfig = ({ columns, onNonWritableFields, onPasteConfigs }: UsePasteConfigProps) => {
  const { getUUIDByCode } = useExcel();
  const { entity } = useAuth();

  const fetchDetailByEndpoint = React.useCallback(
    async (endpoint: string, uuid: string) => {
      if (!entity?.slug || !uuid) return null;

      try {
        const response = await api.get(`/entities/${entity.slug}/erp/${endpoint}/${uuid}/`);
        return response.data;
      } catch {
        return null;
      }
    },
    [entity?.slug]
  );

  const fetchDetailsBatch = React.useCallback(
    async (endpoint: string, uuids: string[], concurrencyLimit: number = 10) => {
      if (!entity?.slug || !uuids.length) return [];

      const results: (any | null)[] = new Array(uuids.length);

      for (let i = 0; i < uuids.length; i += concurrencyLimit) {
        const batch = uuids.slice(i, i + concurrencyLimit);
        const batchStartIdx = i;

        const batchPromises = batch.map(async (uuid, batchIdx) => {
          const originalIdx = batchStartIdx + batchIdx;
          try {
            const response = await api.get(`/entities/${entity.slug}/erp/${endpoint}/${uuid}/`);
            return { idx: originalIdx, data: response.data };
          } catch {
            return { idx: originalIdx, data: null };
          }
        });

        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach(({ idx, data }) => {
          results[idx] = data;
        });
      }

      return results;
    },
    [entity?.slug]
  );

  React.useEffect(() => {
    if (!onNonWritableFields) return;
    const nonWritable = new Set<string>(columns.filter(col => (col as any).nonWritable).map(col => col.field));
    onNonWritableFields(nonWritable);
  }, [columns, onNonWritableFields]);

  React.useEffect(() => {
    if (!onPasteConfigs) return;
    const configs: Record<string, { resolver: (codes: string[]) => Promise<any[]>; resultField?: string }> = {};

    columns.forEach(col => {
      const cfg = (col as any).pasteConfig;
      if (cfg?.resolver) {
        configs[col.field] = {
          resultField: cfg.resultField,
          resolver: cfg.resolver
        };
        return;
      }

      if (cfg?.lookupKey) {
        configs[col.field] = {
          resultField: cfg.resultField,
          resolver: async codes => {
            const payload: Record<string, any> = { main: { [cfg.lookupKey]: codes } };
            const res = await getUUIDByCode(payload);
            const uuids: string[] | undefined = (res && (res as any).main?.[cfg.lookupKey]) || undefined;

            if (cfg.detailEndpoint && uuids?.length) {
              if (uuids.length > 5) {
                const details = await fetchDetailsBatch(cfg.detailEndpoint, uuids);
                return details.map((detail, idx) => detail || uuids[idx]);
              } else {
                const results = await Promise.all(
                  uuids.map(async (uuid, idx) => {
                    const detail = await fetchDetailByEndpoint(cfg.detailEndpoint, uuid);
                    return detail || uuids[idx];
                  })
                );
                return results;
              }
            }

            return uuids || codes;
          }
        };
      }
    });

    onPasteConfigs(configs);
  }, [columns, fetchDetailByEndpoint, fetchDetailsBatch, getUUIDByCode, onPasteConfigs]);

  return columns;
};
